/**
 * Customer Detail Page - Enhanced SaaS Customer Management
 * 
 * Full customer view with:
 * - General info & notes
 * - Subscription details & history
 * - Billing history & invoices
 * - Associated riads with "Open Tenant" button
 * - Users & access
 * - Alerts & action items
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Building2, Users, CreditCard, Activity, FileText,
  RefreshCw, Mail, Phone, Building, Edit, Pause, Play, Plus,
  AlertTriangle, Clock, Calendar, ExternalLink, CheckCircle,
  XCircle, Bell, Send, Eye, MoreVertical, ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { api } from '../../App';
import { toast } from 'sonner';
import { format, differenceInDays, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';

// Status colors
const STATUS_COLORS = {
  ACTIVE: 'bg-green-100 text-green-800',
  SUSPENDED: 'bg-orange-100 text-orange-800',
  CHURNED: 'bg-red-100 text-red-800',
  TRIAL: 'bg-blue-100 text-blue-800',
  EXPIRED: 'bg-red-100 text-red-800'
};

const INVOICE_STATUS_COLORS = {
  DRAFT: 'bg-slate-100 text-slate-700',
  SENT: 'bg-blue-100 text-blue-700',
  PAID: 'bg-green-100 text-green-700',
  OVERDUE: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-gray-100 text-gray-500'
};

const CustomerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [invoiceStats, setInvoiceStats] = useState(null);
  const [plans, setPlans] = useState([]);
  
  // Dialogs
  const [showAddRiadDialog, setShowAddRiadDialog] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [openTenantTarget, setOpenTenantTarget] = useState(null);
  
  // Form
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchCustomer();
    fetchPlans();
    fetchInvoices();
  }, [id]);

  const fetchCustomer = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/customers/${id}`);
      setData(data);
      setAdminNotes(data.account?.admin_notes || '');
    } catch (error) {
      toast.error('Erreur lors du chargement');
      navigate('/admin/customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const { data } = await api.get('/admin/plans?status=ACTIVE');
      setPlans(data.plans || []);
    } catch (error) {
      console.error('Error loading plans');
    }
  };

  const fetchInvoices = async () => {
    try {
      const { data } = await api.get(`/admin/accounts/${id}/invoices`);
      setInvoices(data.invoices || []);
      setInvoiceStats(data.stats);
    } catch (error) {
      console.error('Error loading invoices');
    }
  };

  const handleSaveNotes = async () => {
    try {
      await api.put(`/admin/accounts/${id}`, { admin_notes: adminNotes });
      toast.success('Notes sauvegardées');
      setShowNotesDialog(false);
      fetchCustomer();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleOpenTenant = async (riad) => {
    try {
      // Log the impersonation action with full metadata
      await api.post('/admin/audit/log', {
        action: 'IMPERSONATE_TENANT',
        entity_type: 'Riad',
        entity_id: riad.id,
        description: `Super Admin a ouvert le tenant "${riad.name}" depuis la fiche client ${data.account.name}`,
        metadata: {
          riad_id: riad.id,
          riad_name: riad.name,
          customer_id: id,
          customer_name: data.account.name,
          source: 'customer_detail_page',
          timestamp: new Date().toISOString()
        }
      });
      
      // Store in localStorage for tenant context switch
      localStorage.setItem('impersonatedRiad', JSON.stringify({
        id: riad.id,
        name: riad.name,
        from: 'customer_detail',
        customer_id: id,
        customer_name: data.account.name,
        timestamp: new Date().toISOString()
      }));
      
      toast.success(`Ouverture du tenant "${riad.name}"`, {
        description: 'Un nouvel onglet s\'ouvre avec le contexte du tenant.'
      });
      
      // Navigate to tenant dashboard
      window.open(`/dashboard?riad=${riad.id}`, '_blank');
      
      setOpenTenantTarget(null);
    } catch (error) {
      console.error('Impersonation error:', error);
      toast.error('Erreur lors de l\'ouverture du tenant');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  if (!data) return null;

  const { account, riads, users, managedRiads, activity, stats } = data;
  const subscription = account.subscription;

  // Calculate alerts
  const alerts = [];
  
  // Subscription expiring soon
  if (subscription?.end_date) {
    const daysUntilExpiry = differenceInDays(new Date(subscription.end_date), new Date());
    if (daysUntilExpiry <= 7 && daysUntilExpiry > 0) {
      alerts.push({
        type: 'warning',
        icon: Clock,
        title: 'Abonnement expire bientôt',
        message: `L'abonnement expire dans ${daysUntilExpiry} jour(s)`,
        action: () => navigate('/admin/subscriptions')
      });
    }
    if (daysUntilExpiry <= 0) {
      alerts.push({
        type: 'error',
        icon: XCircle,
        title: 'Abonnement expiré',
        message: `L'abonnement a expiré le ${format(new Date(subscription.end_date), 'dd/MM/yyyy')}`,
        action: () => navigate('/admin/subscriptions')
      });
    }
  }
  
  // Overdue invoices
  const overdueInvoices = invoices.filter(i => i.status === 'OVERDUE');
  if (overdueInvoices.length > 0) {
    const totalOverdue = overdueInvoices.reduce((sum, i) => sum + parseFloat(i.amount_ttc), 0);
    alerts.push({
      type: 'error',
      icon: FileText,
      title: 'Factures en retard',
      message: `${overdueInvoices.length} facture(s) impayée(s) - ${totalOverdue.toLocaleString('fr-FR')} MAD`,
      action: () => document.querySelector('[value="billing"]')?.click()
    });
  }
  
  // Suspended riads
  const suspendedRiads = riads.filter(r => r.status === 'SUSPENDED');
  if (suspendedRiads.length > 0) {
    alerts.push({
      type: 'warning',
      icon: Pause,
      title: 'Tenants suspendus',
      message: `${suspendedRiads.length} riad(s) suspendu(s)`,
      action: () => document.querySelector('[value="riads"]')?.click()
    });
  }
  
  // Incomplete setups
  const incompleteSetups = riads.filter(r => r.setup_state !== 'COMPLETED');
  if (incompleteSetups.length > 0) {
    alerts.push({
      type: 'info',
      icon: AlertTriangle,
      title: 'Setup incomplet',
      message: `${incompleteSetups.length} riad(s) avec setup à terminer`,
      action: () => document.querySelector('[value="riads"]')?.click()
    });
  }

  return (
    <div className="p-6 space-y-6" data-testid="customer-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/customers')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{account.name}</h1>
            <Badge variant="outline">{account.type}</Badge>
            <Badge className={STATUS_COLORS[account.status]}>{account.status}</Badge>
          </div>
          <p className="text-muted-foreground">{account.company || 'Client SaaS'} • Créé le {format(new Date(account.createdAt), 'dd MMMM yyyy', { locale: fr })}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowNotesDialog(true)}>
            <Edit className="w-4 h-4 mr-2" />
            Notes
          </Button>
          {account.status === 'ACTIVE' ? (
            <Button variant="outline" className="text-orange-600">
              <Pause className="w-4 h-4 mr-2" />
              Suspendre
            </Button>
          ) : (
            <Button variant="outline" className="text-green-600">
              <Play className="w-4 h-4 mr-2" />
              Réactiver
            </Button>
          )}
        </div>
      </div>

      {/* Alerts Banner */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((alert, idx) => (
            <div
              key={idx}
              className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                alert.type === 'error' ? 'bg-red-50 border border-red-200 hover:bg-red-100' :
                alert.type === 'warning' ? 'bg-amber-50 border border-amber-200 hover:bg-amber-100' :
                'bg-blue-50 border border-blue-200 hover:bg-blue-100'
              }`}
              onClick={alert.action}
              data-testid={`alert-${idx}`}
            >
              <div className="flex items-center gap-3">
                <alert.icon className={`w-5 h-5 ${
                  alert.type === 'error' ? 'text-red-600' :
                  alert.type === 'warning' ? 'text-amber-600' : 'text-blue-600'
                }`} />
                <div>
                  <p className="font-medium">{alert.title}</p>
                  <p className="text-sm text-muted-foreground">{alert.message}</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </div>
          ))}
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Building2 className="w-8 h-8 mx-auto text-blue-600 mb-2" />
            <p className="text-2xl font-bold">{stats.riad_count}</p>
            <p className="text-sm text-muted-foreground">Riads</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="w-8 h-8 mx-auto text-purple-600 mb-2" />
            <p className="text-2xl font-bold">{stats.user_count}</p>
            <p className="text-sm text-muted-foreground">Utilisateurs</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <CreditCard className="w-8 h-8 mx-auto text-green-600 mb-2" />
            <p className="text-2xl font-bold">
              {subscription ? `${subscription.monthly_price}` : '-'}
            </p>
            <p className="text-sm text-muted-foreground">{subscription?.currency || 'MAD'}/mois</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <FileText className="w-8 h-8 mx-auto text-amber-600 mb-2" />
            <p className="text-2xl font-bold">{invoiceStats?.total_paid?.toLocaleString('fr-FR') || 0}</p>
            <p className="text-sm text-muted-foreground">Total facturé</p>
          </CardContent>
        </Card>
        <Card className={overdueInvoices.length > 0 ? 'border-red-300' : ''}>
          <CardContent className="p-4 text-center">
            <AlertTriangle className={`w-8 h-8 mx-auto mb-2 ${overdueInvoices.length > 0 ? 'text-red-600' : 'text-gray-400'}`} />
            <p className={`text-2xl font-bold ${overdueInvoices.length > 0 ? 'text-red-600' : ''}`}>
              {overdueInvoices.length}
            </p>
            <p className="text-sm text-muted-foreground">Impayé(s)</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="info" className="space-y-4">
        <TabsList>
          <TabsTrigger value="info">Informations</TabsTrigger>
          <TabsTrigger value="subscription">Abonnement</TabsTrigger>
          <TabsTrigger value="billing">
            Facturation
            {overdueInvoices.length > 0 && (
              <Badge className="ml-2 bg-red-100 text-red-700">{overdueInvoices.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="riads">Riads ({riads.length})</TabsTrigger>
          <TabsTrigger value="users">Utilisateurs ({users.length})</TabsTrigger>
          {account.type === 'CONCIERGERIE' && (
            <TabsTrigger value="managed">Riads Gérés ({managedRiads?.length || 0})</TabsTrigger>
          )}
          <TabsTrigger value="activity">Activité</TabsTrigger>
        </TabsList>

        {/* Info Tab */}
        <TabsContent value="info">
          <div className="grid grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Informations du Client</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Nom</p>
                    <p className="font-medium">{account.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Société</p>
                    <p className="font-medium">{account.company || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Mail className="w-4 h-4" /> Email
                    </p>
                    <p className="font-medium">{account.email || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Phone className="w-4 h-4" /> Téléphone
                    </p>
                    <p className="font-medium">{account.phone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email facturation</p>
                    <p className="font-medium">{account.billing_email || account.email || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Type</p>
                    <Badge variant="outline">{account.type}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Notes Internes</CardTitle>
                <CardDescription>Notes visibles uniquement par les super admins</CardDescription>
              </CardHeader>
              <CardContent>
                {account.admin_notes ? (
                  <pre className="text-sm whitespace-pre-wrap bg-muted p-3 rounded max-h-48 overflow-auto">
                    {account.admin_notes}
                  </pre>
                ) : (
                  <p className="text-muted-foreground text-sm">Aucune note</p>
                )}
                <Button variant="outline" size="sm" className="mt-4" onClick={() => setShowNotesDialog(true)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Modifier les notes
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Subscription Tab */}
        <TabsContent value="subscription">
          <Card>
            <CardHeader>
              <CardTitle>Détails de l'Abonnement</CardTitle>
            </CardHeader>
            <CardContent>
              {subscription ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-4 gap-6">
                    <div>
                      <p className="text-sm text-muted-foreground">Plan</p>
                      <Badge 
                        className="mt-1"
                        style={{ 
                          backgroundColor: `${subscription.planDetails?.color}20`,
                          color: subscription.planDetails?.color 
                        }}
                      >
                        {subscription.planDetails?.name || subscription.plan}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Statut</p>
                      <Badge className={`mt-1 ${STATUS_COLORS[subscription.status] || ''}`}>
                        {subscription.status}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Prix mensuel</p>
                      <p className="font-medium text-lg">{subscription.monthly_price} {subscription.currency}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Cycle</p>
                      <p className="font-medium">{subscription.billing_cycle || 'Mensuel'}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-6 pt-4 border-t">
                    <div>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Calendar className="w-4 h-4" /> Date de début
                      </p>
                      <p className="font-medium">
                        {subscription.start_date ? format(new Date(subscription.start_date), 'dd MMMM yyyy', { locale: fr }) : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Calendar className="w-4 h-4" /> Date d'expiration
                      </p>
                      <p className={`font-medium ${
                        subscription.end_date && differenceInDays(new Date(subscription.end_date), new Date()) <= 7 
                          ? 'text-red-600' : ''
                      }`}>
                        {subscription.end_date ? format(new Date(subscription.end_date), 'dd MMMM yyyy', { locale: fr }) : 'Illimité'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Clock className="w-4 h-4" /> Jours restants
                      </p>
                      <p className={`font-medium ${
                        subscription.end_date && differenceInDays(new Date(subscription.end_date), new Date()) <= 7 
                          ? 'text-red-600' : ''
                      }`}>
                        {subscription.end_date ? differenceInDays(new Date(subscription.end_date), new Date()) : '∞'}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <CreditCard className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Aucun abonnement actif</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing">
          <div className="space-y-6">
            {/* Billing Stats */}
            <div className="grid grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">Total facturé</p>
                  <p className="text-2xl font-bold">{invoiceStats?.total_paid?.toLocaleString('fr-FR') || 0} MAD</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">En attente</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {(invoiceStats?.by_status?.SENT?.total || 0).toLocaleString('fr-FR')} MAD
                  </p>
                </CardContent>
              </Card>
              <Card className={overdueInvoices.length > 0 ? 'border-red-300' : ''}>
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">En retard</p>
                  <p className={`text-2xl font-bold ${overdueInvoices.length > 0 ? 'text-red-600' : ''}`}>
                    {(invoiceStats?.by_status?.OVERDUE?.total || 0).toLocaleString('fr-FR')} MAD
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">Factures</p>
                  <p className="text-2xl font-bold">{invoices.length}</p>
                </CardContent>
              </Card>
            </div>
            
            {/* Invoice List */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Historique de Facturation</CardTitle>
                  <CardDescription>Liste des factures de ce client</CardDescription>
                </div>
                <Button onClick={() => navigate('/admin/billing')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle facture
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                {invoices.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Aucune facture</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Numéro</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Échéance</TableHead>
                        <TableHead className="text-right">Montant</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoices.map(invoice => (
                        <TableRow key={invoice.id}>
                          <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                          <TableCell>{format(new Date(invoice.issue_date), 'dd/MM/yyyy')}</TableCell>
                          <TableCell className={invoice.status === 'OVERDUE' ? 'text-red-600' : ''}>
                            {format(new Date(invoice.due_date), 'dd/MM/yyyy')}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {parseFloat(invoice.amount_ttc).toLocaleString('fr-FR')} {invoice.currency}
                          </TableCell>
                          <TableCell>
                            <Badge className={INVOICE_STATUS_COLORS[invoice.status]}>
                              {invoice.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="icon" onClick={() => navigate(`/admin/billing`)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Riads Tab - With Open Tenant Button */}
        <TabsContent value="riads">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Riads Associés</CardTitle>
                <CardDescription>Tenants appartenant à ce client</CardDescription>
              </div>
              <Button onClick={() => setShowAddRiadDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un riad
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {riads.length === 0 ? (
                <div className="text-center py-8">
                  <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Aucun riad</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Riad</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Setup</TableHead>
                      <TableHead>Config</TableHead>
                      <TableHead>Utilisateurs</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {riads.map(riad => (
                      <TableRow key={riad.id} data-testid={`riad-row-${riad.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{riad.name}</p>
                            <p className="text-xs text-muted-foreground">{riad.slug}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_COLORS[riad.status] || 'bg-gray-100'}>
                            {riad.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={riad.setup_state === 'COMPLETED' ? 'secondary' : 'outline'}>
                            {riad.setup_state || 'PENDING'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {riad.configStatus ? (
                            <Badge className={riad.configStatus === 'OK' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                              {riad.configStatus}
                            </Badge>
                          ) : '-'}
                        </TableCell>
                        <TableCell>{riad.userCount || '-'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                              onClick={() => setOpenTenantTarget(riad)}
                              data-testid={`open-tenant-btn-${riad.id}`}
                            >
                              <ExternalLink className="w-4 h-4 mr-1" />
                              Ouvrir Tenant
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => navigate(`/admin/riads/${riad.id}`)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle>Utilisateurs Associés</CardTitle>
              <CardDescription>Utilisateurs ayant accès aux riads de ce client</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {users.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Aucun utilisateur</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Utilisateur</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Rôle principal</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Riads</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map(user => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <p className="font-medium">{user.first_name} {user.last_name}</p>
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{user.mainRole || 'N/A'}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={user.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                            {user.active ? 'Actif' : 'Inactif'}
                          </Badge>
                        </TableCell>
                        <TableCell>{user.riadCount || 1}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Managed Riads Tab (for Concierges) */}
        {account.type === 'CONCIERGERIE' && (
          <TabsContent value="managed">
            <Card>
              <CardHeader>
                <CardTitle>Riads Gérés</CardTitle>
                <CardDescription>Riads dont cette conciergerie assure la gestion</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {!managedRiads || managedRiads.length === 0 ? (
                  <div className="text-center py-8">
                    <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Aucun riad géré</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Riad</TableHead>
                        <TableHead>Propriétaire</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {managedRiads.map(riad => (
                        <TableRow key={riad.id}>
                          <TableCell>
                            <p className="font-medium">{riad.name}</p>
                          </TableCell>
                          <TableCell>{riad.ownerName || '-'}</TableCell>
                          <TableCell>
                            <Badge className={STATUS_COLORS[riad.status]}>
                              {riad.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-violet-600"
                              onClick={() => setOpenTenantTarget(riad)}
                            >
                              <ExternalLink className="w-4 h-4 mr-1" />
                              Ouvrir
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Activity Tab */}
        <TabsContent value="activity">
          <Card>
            <CardHeader>
              <CardTitle>Activité Récente</CardTitle>
              <CardDescription>Actions des 30 derniers jours</CardDescription>
            </CardHeader>
            <CardContent>
              {!activity || activity.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Aucune activité récente</p>
              ) : (
                <div className="space-y-3">
                  {activity.map((act, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 rounded hover:bg-muted/50">
                      <Activity className="w-5 h-5 text-muted-foreground mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium">{act.action}</p>
                        <p className="text-sm text-muted-foreground">{act.description}</p>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(act.createdAt), 'dd/MM HH:mm')}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Open Tenant Confirmation Dialog */}
      <AlertDialog open={!!openTenantTarget} onOpenChange={() => setOpenTenantTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ExternalLink className="w-5 h-5 text-violet-600" />
              Ouvrir le tenant "{openTenantTarget?.name}"
            </AlertDialogTitle>
            <AlertDialogDescription>
              Vous allez accéder au dashboard du tenant en tant que super administrateur.
              Cette action sera enregistrée dans le journal d'audit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="bg-muted/50 rounded-lg p-4 my-4">
            <p className="text-sm"><strong>Tenant:</strong> {openTenantTarget?.name}</p>
            <p className="text-sm"><strong>Client:</strong> {account.name}</p>
            <p className="text-sm text-muted-foreground mt-2">
              Un nouvel onglet s'ouvrira avec le contexte du tenant.
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleOpenTenant(openTenantTarget)}
              className="bg-violet-600 hover:bg-violet-700"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Ouvrir le Tenant
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Notes Dialog */}
      <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Notes Internes</DialogTitle>
            <DialogDescription>Notes visibles uniquement par les super admins</DialogDescription>
          </DialogHeader>
          <Textarea
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            rows={6}
            placeholder="Ajouter des notes sur ce client..."
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNotesDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveNotes}>
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Riad Dialog - TODO */}
      <Dialog open={showAddRiadDialog} onOpenChange={setShowAddRiadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un Riad</DialogTitle>
            <DialogDescription>Créer un nouveau riad pour ce client</DialogDescription>
          </DialogHeader>
          <p className="text-center py-8 text-muted-foreground">
            Fonctionnalité à implémenter
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddRiadDialog(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerDetailPage;
